import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { MandantID } from '../types/branded';
import { toMandantID } from '../types/branded';

export interface MandantKontakt {
  name:    string;
  titel:   string;  // z.B. Geschäftsführer, Sicherheitsbeauftragter
  email:   string;
  telefon: string;
}

export interface MandantConfig {
  id:                 MandantID;
  kurz_code:          string;
  name:               string;
  farbe:              string;
  email_domains:      string[];
  location_keywords:  string[];
  title_aliases:      string[];
  aktiv:              boolean;
  // Adresse
  strasse?:           string;
  plz?:               string;
  ort?:               string;
  land?:              string;
  // Kontakt
  telefon?:           string;
  email?:             string;
  // Organisatorisch
  hauptkontakt?:      MandantKontakt;
  fuehrungspersonen?: MandantKontakt[];
  branche?:           string;
  mitarbeiterzahl?:   number;
  bemerkungen?:       string;
}

// Vorkonfigurierte Mandanten (wie im Prompt spezifiziert)
export const DEFAULT_MANDANTEN: MandantConfig[] = [
  {
    id: toMandantID('mandant-ramsauer'),
    kurz_code: 'RAM',
    name: 'Ramsauer & Stürmer OG',
    farbe: '#4285F4',
    email_domains: ['ramsauer.at', 'ramsauer-stuermer.at'],
    location_keywords: ['Schladming', 'Ramsauer'],
    title_aliases: ['Ramsauer', 'RAM', 'R&S'],
    aktiv: true,
    strasse: 'Industriestraße 12',
    plz: '8970',
    ort: 'Schladming',
    land: 'Österreich',
    telefon: '+43 (0)3687 / 22 123',
    email: 'kontakt@ramsauer.at',
    hauptkontakt: {
      name: 'Martin Ramsauer',
      titel: 'Geschäftsführer',
      email: 'martin.ramsauer@ramsauer.at',
      telefon: '+43 (0)3687 / 22 100',
    },
    fuehrungspersonen: [
      { name: 'Martin Ramsauer', titel: 'Geschäftsführer', email: 'martin.ramsauer@ramsauer.at', telefon: '+43 (0)3687 / 22 100' },
      { name: 'Brigitte Stürmer', titel: 'Betriebsrätin', email: 'b.stuermer@ramsauer.at', telefon: '+43 (0)3687 / 22 110' },
    ],
    branche: 'Metallerzeugung & -bearbeitung',
    mitarbeiterzahl: 180,
    bemerkungen: 'Tätigkeiten mit Schweißen und Metallbearbeitung, lärmintensive Produktion',
  },
  {
    id: toMandantID('mandant-amari'),
    kurz_code: 'AMA',
    name: 'Amari GmbH',
    farbe: '#0F9D58',
    email_domains: ['amari.at', 'amari-gmbh.at'],
    location_keywords: ['Amari', 'Liezen'],
    title_aliases: ['Amari', 'AMA'],
    aktiv: true,
    strasse: 'Am Werksgelände 8',
    plz: '8940',
    ort: 'Liezen',
    land: 'Österreich',
    telefon: '+43 (0)3612 / 25 400',
    email: 'info@amari.at',
    hauptkontakt: {
      name: 'Dr. Wolfgang Hochgruber',
      titel: 'Geschäftsführer & Betriebsrat',
      email: 'w.hochgruber@amari.at',
      telefon: '+43 (0)3612 / 25 401',
    },
    fuehrungspersonen: [
      { name: 'Dr. Wolfgang Hochgruber', titel: 'Geschäftsführer', email: 'w.hochgruber@amari.at', telefon: '+43 (0)3612 / 25 401' },
      { name: 'Franz Müller', titel: 'Sicherheitsbeauftragter', email: 'f.mueller@amari.at', telefon: '+43 (0)3612 / 25 420' },
    ],
    branche: 'Chemische Industrie',
    mitarbeiterzahl: 95,
    bemerkungen: 'Lagerung & Verarbeitung chemischer Stoffe, regelmäßige Unfallstatistik überwachung erforderlich',
  },
  {
    id: toMandantID('mandant-planai'),
    kurz_code: 'PLA',
    name: 'Planai-Hochwurzen-Bahnen GmbH',
    farbe: '#DB4437',
    email_domains: ['planai.at'],
    location_keywords: ['Planai', 'Hochwurzen', 'Schladming'],
    title_aliases: ['Planai', 'PLA', 'PHB'],
    aktiv: true,
    strasse: 'Planaistraße 31',
    plz: '8970',
    ort: 'Schladming',
    land: 'Österreich',
    telefon: '+43 (0)3687 / 22 600',
    email: 'personal@planai.at',
    hauptkontakt: {
      name: 'Peter Schröder',
      titel: 'Betriebsleiter',
      email: 'p.schroeder@planai.at',
      telefon: '+43 (0)3687 / 22 610',
    },
    fuehrungspersonen: [
      { name: 'Peter Schröder', titel: 'Betriebsleiter', email: 'p.schroeder@planai.at', telefon: '+43 (0)3687 / 22 610' },
      { name: 'Silke Wagner', titel: 'Sicherheitsbeauftragte', email: 's.wagner@planai.at', telefon: '+43 (0)3687 / 22 615' },
    ],
    branche: 'Seilbahnen & Bergbahnen',
    mitarbeiterzahl: 65,
    bemerkungen: 'Saisonbetrieb, Arbeit in Höhe, regelmäßige Wartung und Sicherheitsprüfungen der Bahnanlagen',
  },
];

interface MandantState {
  aktiverMandant: MandantID | null;
  mandanten: MandantConfig[];
  setAktiverMandant: (id: MandantID | null) => void;
  setMandanten: (mandanten: MandantConfig[]) => void;
  getMandant: (id: MandantID) => MandantConfig | undefined;
  getAktiverMandant: () => MandantConfig | null;
}

export const useMandantStore = create<MandantState>()(
  persist(
    (set, get) => ({
      aktiverMandant: DEFAULT_MANDANTEN[0]?.id ?? null,
      mandanten: DEFAULT_MANDANTEN,

      setAktiverMandant: (id) => { set({ aktiverMandant: id }); },

      setMandanten: (mandanten) => {
        set({ mandanten });
        // Wenn aktiver Mandant nicht mehr existiert → ersten nehmen oder null
        const { aktiverMandant } = get();
        if (aktiverMandant !== null && !mandanten.find(m => m.id === aktiverMandant)) {
          set({ aktiverMandant: mandanten[0]?.id ?? null });
        }
      },

      getMandant: (id) => get().mandanten.find(m => m.id === id),

      getAktiverMandant: () => {
        const { aktiverMandant, mandanten } = get();
        if (!aktiverMandant) return null;
        return mandanten.find(m => m.id === aktiverMandant) ?? null;
      },
    }),
    { name: 'amed-aktiver-mandant', partialize: (s) => ({ aktiverMandant: s.aktiverMandant }) },
  ),
);
